import { html, nothing } from "lit";
import type { SkillParameterInfo, SkillParametersResult } from "../controllers/skills-settings.ts";
import type { SkillMessageMap } from "../controllers/skills.ts";
import type { SkillStatusEntry, SkillStatusReport } from "../types.ts";
import { clampText } from "../format.ts";

type SkillGroup = {
  id: string;
  label: string;
  skills: SkillStatusEntry[];
};

const SKILL_SOURCE_GROUPS: Array<{ id: string; label: string; sources: string[] }> = [
  { id: "workspace", label: "Workspace Skills", sources: ["igloobot-workspace"] },
  { id: "built-in", label: "Built-in Skills", sources: ["igloobot-bundled"] },
  { id: "installed", label: "Installed Skills", sources: ["igloobot-managed"] },
  { id: "extra", label: "Extra Skills", sources: ["igloobot-extra"] },
];

function groupSkills(skills: SkillStatusEntry[]): SkillGroup[] {
  const groups = new Map<string, SkillGroup>();
  for (const def of SKILL_SOURCE_GROUPS) {
    groups.set(def.id, { id: def.id, label: def.label, skills: [] });
  }
  const builtInGroup = SKILL_SOURCE_GROUPS.find((group) => group.id === "built-in");
  const other: SkillGroup = { id: "other", label: "Other Skills", skills: [] };
  for (const skill of skills) {
    const match = skill.bundled
      ? builtInGroup
      : SKILL_SOURCE_GROUPS.find((group) => group.sources.includes(skill.source));
    if (match) {
      groups.get(match.id)?.skills.push(skill);
    } else {
      other.skills.push(skill);
    }
  }
  const ordered = SKILL_SOURCE_GROUPS.map((group) => groups.get(group.id)).filter(
    (group): group is SkillGroup => Boolean(group && group.skills.length > 0),
  );
  if (other.skills.length > 0) {
    ordered.push(other);
  }
  return ordered;
}

export type SkillsProps = {
  loading: boolean;
  report: SkillStatusReport | null;
  error: string | null;
  filter: string;
  edits: Record<string, string>;
  busyKey: string | null;
  messages: SkillMessageMap;
  onFilterChange: (next: string) => void;
  onRefresh: () => void;
  onToggle: (skillKey: string, enabled: boolean) => void;
  onToggleCommand: (skillKey: string, command: boolean) => void;
  onEdit: (skillKey: string, value: string) => void;
  onSaveKey: (skillKey: string) => void;
  onInstall: (skillKey: string, name: string, installId: string) => void;
  // Skill parameter configuration
  configSelectedKey: string | null;
  configParams: SkillParametersResult | null;
  configParamsLoading: boolean;
  configParamsError: string | null;
  configEdits: Record<string, string>;
  configSaving: string | null;
  configSaveMessage: { kind: "success" | "error"; message: string } | null;
  onConfigSelect: (skillKey: string | null) => void;
  onConfigEditParam: (paramName: string, value: string) => void;
  onConfigSaveParam: (skillKey: string, paramName: string) => void;
  onConfigSaveAll: (skillKey: string) => void;
};

export function renderSkills(props: SkillsProps) {
  const skills = props.report?.skills ?? [];
  const filter = props.filter.trim().toLowerCase();
  const filtered = filter
    ? skills.filter((skill) =>
        [skill.name, skill.description, skill.source].join(" ").toLowerCase().includes(filter),
      )
    : skills;
  const groups = groupSkills(filtered);

  return html`
    <section class="card">
      <div class="row" style="justify-content: space-between;">
        <div>
          <div class="card-title">Skills</div>
          <div class="card-sub">Bundled, managed, and workspace skills.</div>
        </div>
        <button class="btn" ?disabled=${props.loading} @click=${props.onRefresh}>
          ${props.loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      <div class="filters" style="margin-top: 14px;">
        <label class="field" style="flex: 1;">
          <span>Filter</span>
          <input
            .value=${props.filter}
            @input=${(e: Event) => props.onFilterChange((e.target as HTMLInputElement).value)}
            placeholder="Search skills"
          />
        </label>
        <div class="muted">${filtered.length} shown</div>
      </div>

      ${
        props.error
          ? html`<div class="callout danger" style="margin-top: 12px;">${props.error}</div>`
          : nothing
      }

      ${
        filtered.length === 0
          ? html`
              <div class="muted" style="margin-top: 16px">No skills found.</div>
            `
          : html`
            <div class="agent-skills-groups" style="margin-top: 16px;">
              ${groups.map((group) => {
                const collapsedByDefault = group.id === "workspace" || group.id === "built-in";
                return html`
                  <details class="agent-skills-group" ?open=${!collapsedByDefault}>
                    <summary class="agent-skills-header">
                      <span>${group.label}</span>
                      <span class="muted">${group.skills.length}</span>
                    </summary>
                    <div class="list skills-grid">
                      ${group.skills.map((skill) => renderSkill(skill, props))}
                    </div>
                  </details>
                `;
              })}
            </div>
          `
      }
    </section>
  `;
}

function renderSkill(skill: SkillStatusEntry, props: SkillsProps) {
  const busy = props.busyKey === skill.skillKey;
  const apiKey = props.edits[skill.skillKey] ?? "";
  const message = props.messages[skill.skillKey] ?? null;
  const canInstall = skill.install.length > 0 && skill.missing.bins.length > 0;
  const showBundledBadge = Boolean(skill.bundled && skill.source !== "igloobot-bundled");
  const missing = [
    ...skill.missing.bins.map((b) => `bin:${b}`),
    ...skill.missing.env.map((e) => `env:${e}`),
    ...skill.missing.config.map((c) => `config:${c}`),
    ...skill.missing.os.map((o) => `os:${o}`),
  ];
  const reasons: string[] = [];
  if (skill.disabled) {
    reasons.push("disabled");
  }
  if (skill.blockedByAllowlist) {
    reasons.push("blocked by allowlist");
  }
  const isConfigSelected = props.configSelectedKey === skill.skillKey;

  return html`
    <div class="list-item" style="flex-direction: column;">
      <div style="display: flex; width: 100%;">
        <div class="list-main">
          <div class="list-title">
            ${skill.emoji ? `${skill.emoji} ` : ""}${skill.name}
          </div>
          <div class="list-sub">${clampText(skill.description, 140)}</div>
          <div class="chip-row" style="margin-top: 6px;">
            <span class="chip">${skill.source}</span>
            ${
              showBundledBadge
                ? html`
                    <span class="chip">bundled</span>
                  `
                : nothing
            }
            <span class="chip ${skill.eligible ? "chip-ok" : "chip-warn"}">
              ${skill.eligible ? "eligible" : "blocked"}
            </span>
            ${
              skill.disabled
                ? html`
                    <span class="chip chip-warn">disabled</span>
                  `
                : nothing
            }
          </div>
          ${
            missing.length > 0
              ? html`
                <div class="muted" style="margin-top: 6px;">
                  Missing: ${missing.join(", ")}
                </div>
              `
              : nothing
          }
          ${
            reasons.length > 0
              ? html`
                <div class="muted" style="margin-top: 6px;">
                  Reason: ${reasons.join(", ")}
                </div>
              `
              : nothing
          }
        </div>
        <div class="list-meta">
          <div class="row" style="justify-content: flex-end; flex-wrap: wrap; gap: 6px;">
            <button
              class="btn"
              ?disabled=${busy}
              @click=${() => props.onToggle(skill.skillKey, skill.disabled)}
            >
              ${skill.disabled ? "Enable" : "Disable"}
            </button>
            <button
              class="btn"
              ?disabled=${busy}
              @click=${() => props.onToggleCommand(skill.skillKey, skill.command !== true)}
            >
              ${skill.command === true ? "Disable /command" : skill.command === false ? "Enable /command" : "/command: auto"}
            </button>
            ${
              canInstall
                ? html`<button
                  class="btn"
                  ?disabled=${busy}
                  @click=${() => props.onInstall(skill.skillKey, skill.name, skill.install[0].id)}
                >
                  ${busy ? "Installing…" : skill.install[0].label}
                </button>`
                : nothing
            }
            ${
              skill.hasParameters
                ? html`<button
                    class="btn ${isConfigSelected ? "primary" : ""}"
                    @click=${() => props.onConfigSelect(isConfigSelected ? null : skill.skillKey)}
                  >
                    ${isConfigSelected ? "Close Config" : "Configure"}
                  </button>`
                : nothing
            }
          </div>
          ${
            message
              ? html`<div
                class="muted"
                style="margin-top: 8px; color: ${
                  message.kind === "error"
                    ? "var(--danger-color, #d14343)"
                    : "var(--success-color, #0a7f5a)"
                };"
              >
                ${message.message}
              </div>`
              : nothing
          }
          ${
            skill.primaryEnv
              ? html`
                <div class="field" style="margin-top: 10px;">
                  <span>API key</span>
                  <input
                    type="password"
                    .value=${apiKey}
                    @input=${(e: Event) =>
                      props.onEdit(skill.skillKey, (e.target as HTMLInputElement).value)}
                  />
                </div>
                <button
                  class="btn primary"
                  style="margin-top: 8px;"
                  ?disabled=${busy}
                  @click=${() => props.onSaveKey(skill.skillKey)}
                >
                  Save key
                </button>
              `
              : nothing
          }
        </div>
      </div>
      ${isConfigSelected ? renderSkillConfig(skill, props) : nothing}
    </div>
  `;
}

function renderSkillConfig(skill: SkillStatusEntry, props: SkillsProps) {
  const params = props.configParams;
  const loading = props.configParamsLoading;
  const error = props.configParamsError;

  return html`
    <div style="width: 100%; border-top: 1px solid var(--glass-border, var(--border-color)); margin-top: 12px; padding-top: 12px;">
      <div class="card-title" style="font-size: 0.95em; margin-bottom: 8px;">Configuration</div>

      ${
        loading
          ? html`
              <div class="muted">Loading parameters...</div>
            `
          : nothing
      }
      ${error ? html`<div class="callout danger">${error}</div>` : nothing}

      ${
        params && params.parameters.length === 0
          ? html`
              <div class="muted">
                This skill has no configurable parameters. It only needs to be enabled/disabled.
              </div>
            `
          : nothing
      }

      ${
        params && params.parameters.length > 0
          ? html`
            <div style="display: flex; flex-direction: column; gap: 12px;">
              ${params.parameters.map((param) => renderConfigParam(param, skill.skillKey, props))}
            </div>
            <div class="row" style="margin-top: 12px; justify-content: flex-end; gap: 8px;">
              <button
                class="btn primary"
                ?disabled=${props.configSaving != null}
                @click=${() => props.onConfigSaveAll(skill.skillKey)}
              >
                ${props.configSaving === "__all__" ? "Saving..." : "Save All"}
              </button>
            </div>
          `
          : nothing
      }

      ${
        props.configSaveMessage
          ? html`
            <div
              class="callout ${props.configSaveMessage.kind === "success" ? "info" : "danger"}"
              style="margin-top: 8px;"
            >
              ${props.configSaveMessage.message}
            </div>
          `
          : nothing
      }
    </div>
  `;
}

function renderConfigParam(param: SkillParameterInfo, skillKey: string, props: SkillsProps) {
  const value = props.configEdits[param.name] ?? "";
  const isBusy = props.configSaving === param.name;
  const inputType = param.type === "apiKey" || param.type === "secret" ? "password" : "text";

  return html`
    <div class="field">
      <span>
        ${param.label}
        ${
          param.required
            ? html`
                <span style="color: var(--danger-color, #d14343)">*</span>
              `
            : nothing
        }
      </span>
      ${param.description ? html`<div class="muted" style="font-size: 0.85em; margin-bottom: 4px;">${param.description}</div>` : nothing}
      ${
        param.type === "enum" && param.options
          ? html`
            <select
              .value=${value}
              @change=${(e: Event) =>
                props.onConfigEditParam(param.name, (e.target as HTMLSelectElement).value)}
            >
              <option value="">-- Select --</option>
              ${param.options.map(
                (opt) => html`<option value=${opt} ?selected=${value === opt}>${opt}</option>`,
              )}
            </select>
          `
          : html`
            <div class="row" style="gap: 8px;">
              <input
                type=${inputType}
                .value=${value}
                placeholder=${param.default ?? ""}
                @input=${(e: Event) =>
                  props.onConfigEditParam(param.name, (e.target as HTMLInputElement).value)}
                style="flex: 1;"
              />
              <button
                class="btn"
                ?disabled=${isBusy || props.configSaving != null}
                @click=${() => props.onConfigSaveParam(skillKey, param.name)}
                style="flex-shrink: 0;"
              >
                ${isBusy ? "..." : "Save"}
              </button>
            </div>
          `
      }
    </div>
  `;
}
